import React from "react";
import DataTable from "./DataTable";
import { pullCanvasserEvents } from "../../../store/api/calls";
import Moment from "moment";
import { startCase } from "lodash";
import BaseModal from "../modals/BaseModal";
import RowCard from "../../cards/rowCard";
import ImageThumbnail from "../../imgs/thumbnail";
import CanvasserEventsTimeline from "../charts/canvasserEventsTimeline";
import { getEventColorStyle } from "../../../utils/eventColors";
import { Checkbox, FormControlLabel, Box } from "@mui/material";

const GapWarningLine = ({ minutes }) => {
  return <div className={"gap-warning-line"}>{minutes} Minute Gap</div>;
};

export default class DataTableWModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      modalData: [],
      modalTitle: "",
      showLocationUpdates: true,
      showEvents: true,
      lastModalTrigger: null, // Store the data that triggered the modal
    };

    this.isLoadingContent = false;
  }

  showEventsModal = (d) => {
    if (!d._data) return;

    if (this.isLoadingContent) return;

    this.isLoadingContent = true;

    pullCanvasserEvents({
      campaign: d._data.office_id,
      start: new Moment(this.props.events_modal_from).format("YYYY-MM-DD"),
      end: new Moment(this.props.events_modal_to).format("YYYY-MM-DD"),
      canvasser: d._data.id,
      details: 1,
      showEvents: this.state.showEvents,
      showLocationUpdates: this.state.showLocationUpdates,
    })
      .then((data) => {
        if (!data) return;
        let title = d.first_name + " " + d._last_name;
        let processed_data = data
          .sort((a, b) => (a.phone_time > b.phone_time ? 1 : -1))
          .map((d) => ({ ...d, color: getEventColorStyle(d.event_type) }));

        this.setState({
          modalOpen: true,
          modalData: processed_data,
          modalTitle: title + "'s Events",
          lastModalTrigger: d, // Store the trigger data for re-fetching
        });
      })
      .finally((d) => (this.isLoadingContent = false));
  };

  modalClose = () => {
    this.setState({ modalOpen: false });
  };

  handleFilterChange = (filterType) => {
    this.setState(prevState => {
      const newState = {
        [filterType]: !prevState[filterType]
      };
      
      // If both filters would be false after this change, set both to true
      const otherFilterType = filterType === 'showEvents' ? 'showLocationUpdates' : 'showEvents';
      const otherFilterValue = prevState[otherFilterType];
      const newFilterValue = newState[filterType];
      
      if (!newFilterValue && !otherFilterValue) {
        newState.showEvents = true;
        newState.showLocationUpdates = true;
      }
      
      return newState;
    }, () => {
      // Trigger new API call with updated filters if modal is open
      if (this.state.modalOpen && this.state.lastModalTrigger) {
        this.showEventsModal(this.state.lastModalTrigger);
      }
    });
  };


  makeResponseHTML = (element) => {
    if (!element.response || !element.response.length)
      return <span>{element.status || "--"}</span>;

    const filterResponseForMedia = (d) => !!d.media_src;

    //we need to sort out the media files from the text;
    let text_responses = element.response.filter(
      (d) => !filterResponseForMedia(d)
    );
    let media_responses = element.response.filter((d) =>
      filterResponseForMedia(d)
    );
    
    // If no text or media responses, show "--"
    if (!text_responses.length && !media_responses.length) {
      return <span>--</span>;
    }
    
    return (
      <div>
        {!!text_responses.length && (
          <div>{text_responses.map((d) => d.response).join(", ")}</div>
        )}
        {!!media_responses.length && (
          <div className={"flx"}>
            {media_responses.map((d) => (
              <ImageThumbnail
                key={d.id}
                src={d.media_thumb_src || d.media_src}
                largeSrc={d.media_src}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  renderRowCards = () => {
    let rowCards = this.state.modalData.map((d, idx, arr) => {
      let columns = [
        {
          title: "Event Type",
          text: `${startCase(d.event_type)}`,
          textStyle: { fontSize: ".7em" },
        },
        {
          title: "Time",
          text: `${new Moment(d.phone_time).format("MM/DD LT")}`,
          textStyle: { fontSize: ".7em" },
        },
      ];

      if (d.door) {
        columns.push({
          title: "Site/Door",
          text: `${d.door.street_address_1}`,
          textStyle: { fontSize: ".7em" },
        });
      } else if (d.site) {
        columns.push({
          title: "Site/Door",
          text: <div>
            <div style={{fontWeight:600}}>{d.site.name}</div>
            <div style={{fontSize:'90%', fontWeight:400}} >
                {d.site.addressline1} {d.site.addressline2} <br />
                {d.site.city}, {d.site.statecode} {d.site.zip5}
            </div>
          </div>,
          textStyle: { fontSize: ".7em" },
        });
      } else if (["training_start", "training_end"].includes(d.event_type)) {
        columns.push({
          title: "Trainee",
          text: d?.ex_info?.trainee_name || "--",
          textStyle: { fontSize: ".7em" },
        });
      } else {
        columns.push({
          title: "Site/Door",
          text: "--",
          textStyle: { fontSize: ".7em" },
        });
      }

      columns.push({
        title: "Response",
        html: this.makeResponseHTML(d),
        // text: `${(d.response && d.response.length) ? d.response.map((d) => d.response).join(", ") : d.status ? d.status : '--'}`,
        textStyle: { fontSize: ".7em" },
      });

      let gap_to_next = 0;
      if (idx < arr.length - 1) {
        gap_to_next = Moment(arr[idx + 1].phone_time).diff(
          Moment(d.phone_time),
          "minutes"
        );
      }
      return (
        <>
          <RowCard
            color={getEventColorStyle(d.event_type, d.status)}
            key={d.id}
            columns={columns}
          />
          {gap_to_next > 15 && <GapWarningLine minutes={gap_to_next} />}
        </>
      );
    });

    return rowCards;
  };

  isWidowGreaterThan24Hours = () => {
    if (this.state.modalData.length < 2) return false;
    try {
      const start = new Moment(this.state.modalData[0].phone_time);
      const end = new Moment(
        this.state.modalData[this.state.modalData.length - 1].phone_time
      );

      return start.add(24, "hours").isBefore(end);
    } catch {
      return false;
    }
  };

  render() {
    return (
      <div>
        <BaseModal
          showCloseIcon={true}
          open={this.state.modalOpen}
          onClose={this.modalClose}
          title={this.state.modalTitle}
          center
          classNames={{ modal: "baseModal" }}
        >
           <Box sx={{ marginBottom: "10px", display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={this.state.showEvents}
                    onChange={() => this.handleFilterChange('showEvents')}
                    color="primary"
                  />
                }
                label="Events"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={this.state.showLocationUpdates}
                    onChange={() => this.handleFilterChange('showLocationUpdates')}
                    color="primary"
                  />
                }
                label="Location Updates"
              />
            </Box>
          <div className={"card"}>
            <h6 style={{ marginBottom: "-20px" }}>Timeline</h6>            
            <CanvasserEventsTimeline
              data={this.state.modalData}
              long={this.isWidowGreaterThan24Hours()}
            />
          </div>

          {this.renderRowCards()}
        </BaseModal>
        <DataTable
          data={this.props.data}
          events_modal={this.showEventsModal}
          title={this.props.title}
        />
      </div>
    );
  }
}
